import { useAppSelector } from "@/store/hooks";
import { selectDataList } from "@/store/ses/sesDataElemSlice";
import { ActionVariantType } from "@/types/ses/sesItemConfigTypes";
import { useLayoutEffect, useMemo } from "react";

interface IUseSearchActionsDataElemByConditionsAndCategoties {
	data: ActionVariantType[];
	searchInputValue: string;
	setSearchInputValue: (values: { searchByDataElem: string }) => void;
}

const useSearchActionsDataElemByConditionsAndCategoties = ({ data, searchInputValue, setSearchInputValue }: IUseSearchActionsDataElemByConditionsAndCategoties) => {
	const dataElemList = useAppSelector(selectDataList); // список КТ

	useLayoutEffect(() => {
		setSearchInputValue({ searchByDataElem: "" });
	}, [data.length]);

	const actionsWithDataElemOptionsInConditions = useMemo(
		() =>
			(data || []).reduce<{ actionId: string; dataElemIdsInConditions: (string | null)[] }[]>((acc, el, index) => {
				const conditionsWithDataType = {
					actionId: el.actionId ?? `${el.action}-${index}`,
					dataElemIdsInConditions:
						el.conditions?.flatMap((conditionGroup) =>
							conditionGroup
								.filter((item) => item.type === "data" || (item.type === "or" && item.value.find((condition) => condition.type === "data")))
								.flatMap((item) =>
									item.type === "data"
										? [item.id]
										: item.type === "or"
										? item.value.filter((condition) => condition.type === "data").map((condition) => ("id" in condition ? condition.id : null))
										: [null]
								)
						) ?? [],
				};

				return [...acc, conditionsWithDataType];
			}, []),
		[JSON.stringify(data)]
	);

	const actionsWithDataElemOptionsInCategories = useMemo(
		() =>
			(data || []).reduce<{ actionId: string; dataElemIdsInConditions: (string | null)[] }[]>((acc, el, index) => {
				const conditionsWithDataType = {
					actionId: el.actionId ?? `${el.action}-${index}`,
					dataElemIdsInConditions:
						"categories" in el
							? Object.values(el.categories).flatMap((el) =>
									el.flatMap((item) =>
										item
											.filter((condition) => condition.type === "data" || condition.type === "or")
											.flatMap((searchedCondition) =>
												searchedCondition.type === "or"
													? searchedCondition.value
															.filter((conditionInOrBlock) => conditionInOrBlock.type === "data")
															.map((conditionDataTypeInOrBlock) => ("id" in conditionDataTypeInOrBlock ? conditionDataTypeInOrBlock.id : null))
													: "id" in searchedCondition
													? searchedCondition.id
													: null
											)
									)
							  )
							: [],
				};

				return [...acc, conditionsWithDataType];
			}, []),
		[JSON.stringify(data)]
	);

	const dataElemsAutocompleteOptions = useMemo(() => {
		if (!data || !dataElemList?.data) return [];

		return [...actionsWithDataElemOptionsInConditions, ...actionsWithDataElemOptionsInCategories]
			.flatMap((el) => el.dataElemIdsInConditions)
			.filter((el) => el !== "" && el !== null)
			.filter((el, index, self) => self.indexOf(el) === index) // убираем дубликаты
			.reduce<{ value: string; label: string }[]>(
				(acc, el) => [
					...acc,
					{
						value: el ?? "",
						label: dataElemList.data.find((item) => item.id === el)?.name ?? "",
					},
				],
				[]
			);
	}, [JSON.stringify([...actionsWithDataElemOptionsInConditions, ...actionsWithDataElemOptionsInCategories]), dataElemList]);

	const visibleActionsIds = useMemo(() => {
		if (!data) return [];

		const allActions = [...actionsWithDataElemOptionsInConditions, ...actionsWithDataElemOptionsInCategories];

		return searchInputValue === ""
			? allActions.flatMap((el) => el.actionId)
			: allActions.reduce<string[]>((acc, el) => (el.dataElemIdsInConditions.includes(searchInputValue) ? [...acc, el.actionId] : acc), []);
	}, [searchInputValue, JSON.stringify(data)]);

	const dataElemIn = {
		runConditions: actionsWithDataElemOptionsInConditions.reduce<string[]>(
			(acc, el) => (el.dataElemIdsInConditions.includes(searchInputValue) ? [...acc, el.actionId] : acc),
			[]
		),
		conditionsForAssigningCategories: actionsWithDataElemOptionsInCategories.reduce<string[]>(
			(acc, el) => (el.dataElemIdsInConditions.includes(searchInputValue) ? [...acc, el.actionId] : acc),
			[]
		),
	};

	return { dataElemsAutocompleteOptions, visibleActionsIds, dataElemIn };
};

export default useSearchActionsDataElemByConditionsAndCategoties;
